import { createApp, configureCompat, h } from 'vue'
import router from './router-auth'
import { createApolloProvider } from '@vue/apollo-option'
import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client/core'
import { getCurrentUser, User } from './user'
import Toast, { useToast } from 'vue-toastification'
import 'vue-toastification/dist/index.css'
/** Vue Select */
import vSelect from 'vue-select'
/**
 * Register auth global components.
 */
import Checkbox from '@/components/auth/Checkbox'
import Places from '@/components/Places'
import Icon from '@/components/IconComponent.vue'
import HeartIcon from '/public/resources/svg/vue/heart.svg?raw'
import PersonIcon from '/public/resources/svg/vue/person.svg?raw'
import Services from '/public/resources/svg/vue/services.svg?raw'
import WriteIcon from '/public/resources/svg/vue/write.svg?raw'

// Global @vue/compat mode 2.
configureCompat({ MODE: 2 })

// Mirror app.js: auth-page components (EmailVerify, already-invited...) embed
// `${icons.write}` in toasts. Without this global the auth bundle throws
// "icons is not defined", which gets swallowed by try/catch and silently breaks
// email verification (the success toast throws before the token is stored).
window.icons = {
    heart: HeartIcon,
    person: PersonIcon,
    services: Services,
    write: WriteIcon,
}

// vue-toastification replaces vue-toasted. Mirror app.js so auth-page components
// (LoginForm, EmailVerify, register wizard...) keep the `this.$toasted` API that the
// Vue 2 global `vue-toasted` plugin used to provide. Without this, `this.$toasted`
// is undefined on auth pages and the first toast call throws.
const toast = useToast()
const toHtml = (msg) => (typeof msg === 'string' ? h('div', { innerHTML: msg }) : msg)
const compatToasted = {
    success: (msg, opts) => toast.success(toHtml(msg), opts),
    error: (msg, opts) => toast.error(toHtml(msg), opts),
    info: (msg, opts) => toast.info(toHtml(msg), opts),
    show: (msg, opts) => toast(toHtml(msg), opts),
}

/**
 * Initialize Apollo for GraphQL
 */

// Name of the localStorage item
const AUTH_TOKEN_NAME = process.env.MIX_APOLLO_TOKEN_NAME || 'apollo-token'

const apolloClient = new ApolloClient({
    link: createHttpLink({
        uri: process.env.MIX_GRAPHQL_URL,
        headers: {
            authorization: localStorage.getItem(AUTH_TOKEN_NAME) || ''
        },
    }),
    cache: new InMemoryCache(),
})

const apolloProvider = createApolloProvider({
    defaultClient: apolloClient,
})

// vue-select 4 custom OpenIndicator (Vue 3 render function uses `h`).
const OpenIndicator = {
    render() {
        return h(
            'svg',
            { width: 24, height: 24, viewBox: '0 0 24 24' },
            [
                h('path', {
                    d: 'M15.8842316,9.00423077 L12,12.88 L8.11576839,9.00423077 C7.72585884,8.61517069 7.09448505,8.61551495 6.705,9.005 C6.31563925,9.39436075 6.31563925,10.0256393 6.705,10.415 L11.6464466,15.3564466 C11.8417088,15.5517088 12.1582912,15.5517088 12.3535534,15.3564466 L17.295,10.415 C17.6843607,10.0256393 17.6843607,9.39436075 17.295,9.005 C16.905515,8.61551495 16.2741412,8.61517069 15.8842316,9.00423077 Z',
                }),
            ]
        )
    },
}

if (vSelect.props && vSelect.props.components) {
    vSelect.props.components.default = () => ({ OpenIndicator })
}

/**
 * Initialize auth application.
 */

 getCurrentUser()
    .then(resp => {
        if (window.location.pathname.includes('impersonate') || window.location.pathname.includes('welcome')) {
            throw new Error('Forcing impersonate by executing the catch callback')
        }

        window.location.href = '/'
    })
    .catch(error => {
        window.currentUser = new User()

        const app = createApp({})

        app.config.globalProperties.AUTH_TOKEN_NAME = AUTH_TOKEN_NAME
        app.config.globalProperties.currentUser = window.currentUser
        app.config.globalProperties.$toasted = compatToasted

        app.use(router)
        app.use(apolloProvider)
        app.use(Toast, {
            position: 'top-right',
            timeout: 6000,
        })

        app.component('v-select', vSelect)
        app.component('checkbox', Checkbox)
        app.component('icon', Icon)
        app.component('places', Places)

        app.mount('#app-auth')
    })
